import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { PERMISSIONS } from "../../utils/permissions";
import { usePermissions } from "../../contexts/PermissionsContext";
import { FieldGroup, FormBuilder } from "react-reactive-form";
import LSQuestionsList from "../../components/LSQuestionsList";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import { useParams } from "react-router-dom";

const LSQuestions = () => {
  const [multipleArm, setMultipleArm] = useState(null);
  const { setAjaxLoading } = useCsr();
  const [missingFields, setMissingFields] = useState([]);
  const {
    questionsDataSaved,
    setQuestionsDataSaved,
    qForm,
    laySummaryWizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();
  const { studyId } = useParams();
  const { hasPermission } = usePermissions();

  // ls:wizards:read gates GET /lay-summaries/{id}/wizards. The PUT replaces the
  // whole wizard, so it needs the read that populated the form as well as
  // ls:wizards:update.
  const canReadWizards = hasPermission(PERMISSIONS.lsWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.lsWizardsUpdate);

  const formArraysKeys = {
    qTgNames: "object",
    qReferences: "array",
  };

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");

  // Ref to communicate with Questions component about save state
  const questionsRef = useRef();

  // Flag to track if we're currently saving to prevent form repopulation
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!canReadWizards) {
      return;
    }

    // Only fetch questions data if we don't already have it
    if (!questionsDataSaved) {
      setAjaxLoading(true);

      CsrAPI.getLaySummaryQuestions({ studyId })
        .then(questionsDataSaved => {
          replaceNullArrays(questionsDataSaved);
          setQuestionsDataSaved(questionsDataSaved);
          setErrMsg("");
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else {
            setErrMsg("Failed to get questions");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    }
  }, [studyId, setQuestionsDataSaved, questionsDataSaved, canReadWizards]);

  const replaceNullArrays = obj => {
    for (let key in obj) {
      if (typeof obj[key] === "object") {
        if (Array.isArray(obj[key])) {
          if (obj[key].every(value => value === null)) {
            obj[key] = [];
          }
        } else {
          replaceNullArrays(obj[key]);
        }
      }
    }
  };

  useEffect(() => {
    // Only retrieve saved wizard state if we're not currently saving
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const constructFormGroupFromObject = obj => {
    const result = {};

    Object.entries(obj).forEach(([key, value]) => (result[key] = value));

    return FormBuilder.group(result);
  };

  const retrieveSavedWizardState = () => {
    // Don't repopulate form if we're currently saving
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const wizardFormValue = questionsDataSaved.wizardForm;

      // Check if the lay summary form has any meaningful content already
      const hasFormContent = Object.keys(laySummaryWizardForm.value).some(
        key => {
          const value = laySummaryWizardForm.value[key];
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return value !== null && value !== "" && value !== undefined;
        }
      );

      // If the form already has content and this is not an initial load, don't repopulate
      if (hasFormContent && laySummaryWizardForm.dirty) {
        return;
      }

      // Clear existing form arrays before repopulating to avoid duplication
      Object.keys(wizardFormValue).forEach(key => {
        if (formArraysKeys[key] && laySummaryWizardForm.controls[key]) {
          // Clear FormArray controls
          while (laySummaryWizardForm.controls[key].length > 0) {
            laySummaryWizardForm.controls[key].removeAt(0);
          }
        }
      });

      // Pre-populate lay summary form fields
      Object.keys(wizardFormValue).map(function (key) {
        if (formArraysKeys[key]) {
          if (formArraysKeys[key] === "object") {
            wizardFormValue[key].forEach(item => {
              laySummaryWizardForm.controls[key].push(
                constructFormGroupFromObject(item)
              );
            });
          } else {
            // Handle array fields
            if (key === "qReferences") {
              // Handle backward compatibility for qReferences
              let referencesArray = wizardFormValue[key];
              if (!Array.isArray(referencesArray)) {
                // Convert old string format to array format
                referencesArray = referencesArray ? [referencesArray] : [""];
              }
              // Ensure at least one empty field if array is empty
              if (referencesArray.length === 0) {
                referencesArray = [""];
              }
              referencesArray.forEach(item => {
                laySummaryWizardForm.controls[key].push(
                  FormBuilder.control(item)
                );
              });
            } else {
              wizardFormValue[key].forEach(item => {
                laySummaryWizardForm.controls[key].push(
                  FormBuilder.control(item)
                );
              });
            }
          }
        } else {
          // For simple fields, patch the value directly
          laySummaryWizardForm.patchValue({ [key]: wizardFormValue[key] });
        }

        return true;
      });
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    // The Save button is disabled and Next navigates without saving, so this
    // only guards a programmatic call.
    if (!canSaveWizards) {
      return;
    }

    // Trigger validation display
    if (laySummaryWizardForm.triggerValidation) {
      laySummaryWizardForm.triggerValidation();
    }

    // Custom validation check for all required fields
    const requiredFields = [
      "qShortTgName",
      "qStudyTitle",
      "qTgNames",
      "qStudyPurpose",
      "qFollowUp",
      "qComparative",
    ];
    const missingFields = [];

    requiredFields.forEach(fieldName => {
      const field = laySummaryWizardForm.controls[fieldName];
      if (!field) {
        missingFields.push(fieldName);
      } else if (Array.isArray(field.value)) {
        if (
          !field.value.some(
            item =>
              item.name != null &&
              item.name !== "" &&
              item.role != null &&
              item.role !== ""
          )
        ) {
          missingFields.push(fieldName);
        }
      } else if (!field.value || field.value === "") {
        missingFields.push(fieldName);
      }
    });

    // Check if qFollowUpDesc is required (when qFollowUp is "true")
    if (laySummaryWizardForm.controls.qFollowUp.value === "true") {
      const followUpDescField = laySummaryWizardForm.controls.qFollowUpDesc;
      if (
        !followUpDescField ||
        !followUpDescField.value ||
        followUpDescField.value === ""
      ) {
        missingFields.push("qFollowUpDesc");
      }
    }

    setMissingFields(missingFields);

    // Check if form is valid
    if (missingFields.length > 0 || !laySummaryWizardForm.valid) {
      setErrMsg("Please fill in required fields before proceeding.");

      // Move cursor to first validation error if there are any
      if (missingFields.length > 0) {
        if (laySummaryWizardForm.moveCursorToFirstError) {
          laySummaryWizardForm.moveCursorToFirstError(missingFields);
        } else {
          console.warn("moveCursorToFirstError function not available");
        }
      }

      // Return early without saving or throwing error
      return;
    }

    // Set saving flag to prevent form repopulation
    setIsSaving(true);

    // Notify Questions component that save operation is starting
    if (questionsRef.current && questionsRef.current.setSavingState) {
      questionsRef.current.setSavingState(true);
    }

    setAjaxLoading(true);
    // Create a copy of the form values to avoid mutating the original form
    const formValuesToSave = JSON.parse(
      JSON.stringify(laySummaryWizardForm.value)
    );

    // Ensure qReferences is in the correct array format for the API
    if (
      formValuesToSave.qReferences &&
      Array.isArray(formValuesToSave.qReferences)
    ) {
      // Filter out empty values
      formValuesToSave.qReferences = formValuesToSave.qReferences.filter(
        ref => ref && ref.trim() !== ""
      );
    }

    const state = {
      // qForm: qForm.value,
      wizardForm: formValuesToSave,
    };

    setFormFilledData(qForm.value);
    setWizardFormFilledData(formValuesToSave);

    // Return the Promise so it can be awaited by navigation handlers
    return CsrAPI.saveLaySummaryQuestions({ studyId, state })
      .then(() => {
        setErrMsg("");
        toast.success("Data saved successfully!");

        // Update the saved data with the current form state to prevent form reset
        // Only update if the data is different to avoid unnecessary re-renders
        if (
          JSON.stringify(questionsDataSaved?.wizardForm) !==
          JSON.stringify(formValuesToSave)
        ) {
          setQuestionsDataSaved({
            ...questionsDataSaved,
            wizardForm: formValuesToSave,
          });
        }

        // Also update the form data to ensure consistency
        setFormFilledData(qForm.value);
        setWizardFormFilledData(formValuesToSave);

        return Promise.resolve(); // Explicitly return resolved promise for success
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to save questions");
        }
        errRef.current?.focus();
        return Promise.reject(error); // Re-throw the error for navigation handler
      })
      .finally(() => {
        setAjaxLoading(false);

        // Reset saving flag
        setIsSaving(false);

        // Notify Questions component that save operation is complete
        if (questionsRef.current && questionsRef.current.setSavingState) {
          questionsRef.current.setSavingState(false);
        }
      });
  };

  const QFormData = ({ qForm }) => {
    return (
      <>
        <FieldGroup
          control={qForm}
          render={() => (
            <>
              {
                <LSQuestionsList
                  ref={questionsRef}
                  multipleArm={multipleArm}
                  setMultipleArm={setMultipleArm}
                  missingFields={missingFields}
                />
              }
            </>
          )}
        />
      </>
    );
  };

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {/* <div className={"questions-content__item"}>
              <div className={"questions-content__label intro-text"}>
                <span className={"questions-content__label-text"}>
                  Please answer all of the questions.
                </span>
              </div>
            </div> */}
            <QFormData qForm={qForm} questionsDataSaved={questionsDataSaved} />
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Anwser the following questions"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Select Study",
        buttonGoToAction: "/ls/" + studyId + "/select-study",
      }}
      nextButton={{
        buttonTitle: "Lay Summary File",
        buttonGoToAction: "/ls/" + studyId + "/lay-summary-file",
      }}
      saveButtonHandler={handleSubmit}
      savePermission={[PERMISSIONS.lsWizardsRead, PERMISSIONS.lsWizardsUpdate]}
      saveButton={true}
    />
  );
};

export default LSQuestions;
